import PaginationSchema from "_shared/_api/queries/pagination/_schema/pagination-parameters-schema";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import ProposalFilterSchema from "work/proposals/_api/queries/_schemas/proposal-filter-schema";
import ProposalOrderSchema from "work/proposals/_api/queries/_schemas/proposal-order-schema";
import { z } from "zod";

export type ProposalQueryJSON = z.infer<typeof ProposalQuerySchema.ProposalQueryJSONZ>;
export type ProposalQueryParams = z.infer<typeof ProposalQuerySchema.ProposalQueryZ>;

export default class ProposalQuerySchema {
  public static readonly ProposalQueryJSONZ = z.object({
    filterParameters: ProposalFilterSchema.ProposalFilterJSONZ,
    orderParameters: ProposalOrderSchema.ProposalOrderJSONZ,
    paginationParameters: PaginationSchema.PaginationZ.nullish(),
  });

  public static readonly ProposalQueryZ = z.object({
    filterParameters: ProposalFilterSchema.ProposalFilterZ,
    orderParameters: ProposalOrderSchema.ProposalOrderZ,
    paginationParameters: PaginationSchema.PaginationZ.optional(),
  });

  public static validate(values: unknown): ProposalQueryParams {
    return parseOrThrow(ProposalQuerySchema.ProposalQueryZ, values, {
      logError: true,
      schemaName: "ProposalQuerySchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): ProposalQueryJSON {
    return parseOrThrow(ProposalQuerySchema.ProposalQueryJSONZ, json, {
      logError: true,
      schemaName: "ProposalQuerySchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is ProposalQueryParams {
    try {
      ProposalQuerySchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is ProposalQueryJSON {
    try {
      ProposalQuerySchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: ProposalQueryJSON): ProposalQueryParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(ProposalQuerySchema.ProposalQueryJSONZ, json, {
      logError: true,
      schemaName: "ProposalQuerySchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return ProposalQuerySchema.codec.decode(parsed);
  }

  public static serialize(params: ProposalQueryParams): ProposalQueryJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = ProposalQuerySchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return ProposalQuerySchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(ProposalQuerySchema.ProposalQueryJSONZ, ProposalQuerySchema.ProposalQueryZ, {
    decode: (data) => ({
      filterParameters: ProposalFilterSchema.deserialize(data.filterParameters),
      orderParameters: ProposalOrderSchema.deserialize(data.orderParameters),
      paginationParameters: data.paginationParameters
        ? PaginationSchema.deserialize(data.paginationParameters)
        : undefined,
    }),
    encode: (query) => ({
      filterParameters: ProposalFilterSchema.serialize(query.filterParameters),
      orderParameters: ProposalOrderSchema.serialize(query.orderParameters),
      paginationParameters: query.paginationParameters
        ? PaginationSchema.serialize(query.paginationParameters)
        : undefined,
    }),
  });
}
